export type BrowserProductDbClient = {
  loadJson: <TValue>(key: string) => Promise<TValue | null>;
  saveJson: <TValue>(key: string, value: TValue) => Promise<void>;
  requestAdmin: <TValue = unknown>(input: {
    method: "GET" | "POST";
    path: string;
    body?: unknown;
  }) => Promise<{ ok: true; data: TValue } | { ok: false; error: string }>;
  listUsers: () => Promise<
    Array<{
      id: number;
      email: string;
      role: "admin" | "user";
      isActive: boolean;
      billingStatus: "active" | "trialing" | "past_due" | "unpaid" | "canceled" | "inactive";
    }>
  >;
  isAdmin?: boolean;
};

import { IS_TEST_MODE } from "../constants";
import { authHeaders } from "../lib/api-client";

const TEST_USERS: Array<{
  id: number;
  email: string;
  role: "admin" | "user";
  isActive: boolean;
  billingStatus: "active" | "trialing" | "past_due" | "unpaid" | "canceled" | "inactive";
}> = [
  {
    id: 1,
    email: "admin@constructorfleet.com",
    role: "admin",
    isActive: true,
    billingStatus: "active",
  },
  {
    id: 2,
    email: "pilot@constructorfleet.com",
    role: "user",
    isActive: true,
    billingStatus: "active",
  },
  { id: 3, email: "newuser@example.com", role: "user", isActive: true, billingStatus: "inactive" },
];

function parseJson<TValue>(raw: string | null): TValue | null {
  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw) as TValue;
  } catch {
    return null;
  }
}

export const browserProductDbClient: BrowserProductDbClient = {
  async loadJson<TValue>(key: string) {
    return parseJson<TValue>(localStorage.getItem(key));
  },
  async saveJson<TValue>(key: string, value: TValue) {
    localStorage.setItem(key, JSON.stringify(value));
  },
  async requestAdmin<TValue>({
    method,
    path,
    body,
  }: {
    method: "GET" | "POST";
    path: string;
    body?: unknown;
  }) {
    if (IS_TEST_MODE) {
      return { ok: true, data: {} as TValue };
    }

    const response = await fetch(path, {
      method,
      headers: authHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    if (!response.ok) {
      const payload = (await response.json().catch(() => null)) as {
        error?: string;
      } | null;
      return { ok: false, error: payload?.error ?? "Request failed." };
    }

    const payload = (await response.json()) as {
      ok?: boolean;
      error?: string;
    } & TValue;
    if (payload.ok === false) {
      return { ok: false, error: payload.error ?? "Request failed." };
    }
    return { ok: true, data: payload };
  },
  async listUsers() {
    if (IS_TEST_MODE) {
      return TEST_USERS;
    }

    const response = await fetch("/api/admin/users", {
      headers: authHeaders(),
    });
    if (!response.ok) {
      return [];
    }

    const payload = (await response.json()) as {
      ok: boolean;
      users: Array<{
        id: number;
        email: string;
        role: "admin" | "user";
        isActive: boolean;
        billingStatus: "active" | "trialing" | "past_due" | "unpaid" | "canceled" | "inactive";
      }>;
    };

    return payload.ok ? payload.users : [];
  },
};
